/**
 * Vista de detalle de evento para Miembros (solo lectura).
 * Muestra info del evento + estado de su propia inscripción.
 * Sin paneles de gestión, inscritos de otros, pagos del club, etc.
 */

import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from "@/components/ui";
import {
  Calendar,
  MapPin,
  Users,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Info,
} from "lucide-react";
import { useEvent, usePersonRegistrations } from "@/hooks/useEvents";
import { useAuthStore } from "@/stores/authStore";
import {
  getEventStatusLabel,
  getEventStatusColor,
} from "@/lib/services/events";
import type { EventRegistration } from "@/types";

const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    timestamp !== null &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (
    typeof timestamp === "object" &&
    timestamp !== null &&
    "seconds" in timestamp
  ) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

const formatLongDate = (date: Date) =>
  date.toLocaleDateString("es-CO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const getRegistrationStatusInfo = (status: EventRegistration["status"]) => {
  switch (status) {
    case "approved":
      return {
        label: "Inscripción aprobada",
        color: "bg-green-100 text-green-700",
        icon: CheckCircle2,
      };
    case "pending":
      return {
        label: "Pendiente de aprobación",
        color: "bg-yellow-100 text-yellow-700",
        icon: Clock,
      };
    case "waitlist":
      return {
        label: "En lista de espera",
        color: "bg-blue-100 text-blue-700",
        icon: AlertCircle,
      };
    case "rejected":
      return {
        label: "Inscripción rechazada",
        color: "bg-red-100 text-red-700",
        icon: XCircle,
      };
    case "cancelled":
      return {
        label: "Inscripción cancelada",
        color: "bg-gray-100 text-gray-500",
        icon: XCircle,
      };
    default:
      return {
        label: status,
        color: "bg-gray-100 text-gray-600",
        icon: AlertCircle,
      };
  }
};

export default function MemberEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuthStore();
  const personId = profile?.personId;

  const { data: event, isLoading } = useEvent(id || "");
  const { data: myRegistrations } = usePersonRegistrations(personId);

  const myRegistration = useMemo(() => {
    if (!myRegistrations || !id) return null;
    return (
      myRegistrations.find(
        (r) =>
          r.eventId === id &&
          ["pending", "approved", "waitlist"].includes(r.status),
      ) || null
    );
  }, [myRegistrations, id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center text-gray-600">
            <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="font-medium">Evento no encontrado</p>
            <Link to="/events">
              <Button variant="ghost" className="mt-3">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver a eventos
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const startDate = toSafeDate(event.startDate);
  const endDate = event.endDate ? toSafeDate(event.endDate) : null;
  const statusLabel = getEventStatusLabel(event.status);
  const statusColor = getEventStatusColor(event.status);
  const regInfo = myRegistration
    ? getRegistrationStatusInfo(myRegistration.status)
    : null;
  const RegIcon = regInfo?.icon;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4">
      {/* Volver */}
      <Link to="/events">
        <Button variant="ghost" size="sm" className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver a eventos
        </Button>
      </Link>

      {/* Estado de inscripción (banner) */}
      {myRegistration && regInfo && RegIcon && (
        <Card className="border-l-4 border-l-primary-500">
          <CardContent className="p-4 flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${regInfo.color}`}
            >
              <RegIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">
                {regInfo.label}
              </p>
              <p className="text-xs text-gray-500">
                Estás registrado en este evento.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info principal */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-xl">{event.name}</CardTitle>
              {event.description && (
                <p className="text-sm text-gray-600 mt-2">
                  {event.description}
                </p>
              )}
            </div>
            <Badge className={statusColor}>{statusLabel}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
            <div>
              <p className="text-gray-700">
                {formatLongDate(startDate)}
                {endDate && ` — ${formatLongDate(endDate)}`}
              </p>
            </div>
          </div>

          {event.location && (
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-700">
                  {event.location.city}
                  {event.location.name ? ` · ${event.location.name}` : ""}
                </p>
                {event.location.address && (
                  <p className="text-xs text-gray-500">
                    {event.location.address}
                  </p>
                )}
              </div>
            </div>
          )}

          {event.registrationConfig?.maxCapacity && (
            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-gray-400 mt-0.5" />
              <p className="text-gray-700">
                Cupos: {event.registrationConfig.maxCapacity}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aviso de inscripción a través del director */}
      {!myRegistration && (
        <Card className="bg-blue-50/40 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold">¿Quieres asistir?</p>
              <p className="mt-1">
                Las inscripciones a eventos las gestiona el director de tu club.
                Contáctalo para que te incluya en este evento.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
